import { Actions, Actions as CommonActions } from "@client/actions";
import api from "@client/app/lib/api";
import mappers from "@client/app/lib/mappers";
import { globals } from "@client/globals";
import { apiAction } from "@client/lib/actions";
import { scrollToY } from "@client/lib/misc";
import { goTo } from "@client/lib/navigation";
import { pick } from "@client/lib/object";
import { processQueryParameters } from "@client/lib/url";
import {
  type AppState,
  type Entities,
  type GenericMap,
  type LocalSettings,
  type MainSettings,
  type Nullable
} from "@client/types";
import { type Api, type InitResponseData } from "@client/types/Api";
import { type ComponentAppState } from "@client/types/AppState/component";
import { type PageAppState } from "@client/types/AppState/page";
import { type PagesAppState } from "@client/types/AppState/pages";
import { type PagePopupAppState, type PopupAppState } from "@client/types/AppState/popups";
import { type PortalAppState } from "@client/types/AppState/portal";
import { type StyleSheetAppState } from "@client/types/AppState/styleSheet";
import appInfo from "@common/appInfo";

export interface WithPagesAppState {
  pages: PagesAppState;
}

export type InitSuccessActionData = Omit<LoadPageSuccessActionData, "pagePopups"> & {
  portal: PortalAppState;
  events: Api.Event[];
  pages: PagesAppState;
  pagePopups: PagePopupAppState[];
  trialEnd?: string;
};
export const init = (resp: InitResponseData) => (dispatch: any, getState: () => AppState) => {
  const isDefaultCulture = resp.cultureCode === appInfo.primaryCultureCode;
  const data: InitSuccessActionData = mappers.init(resp, isDefaultCulture);

  dispatch({
    type: CommonActions.InitSuccess,
    data
  });
};

export const initSsr = () => ({
  type: Actions.SsrInit,
  data: {}
});

export type LoadPageBeginActionData = {
  path: string;
  trackingParameters: GenericMap<string>;
};
export type LoadPageSuccessActionData = Nullable<Api.WithFunnelInfo> & {
  page: PageAppState;
  content: {
    entities: Entities;
    local: LocalSettings;
  };
  layoutContent: {
    settings: MainSettings;
    local: MainSettings | null;
  } | null;
  components: ComponentAppState[] | null;
  popups: PopupAppState[] | null;
  styleSheet: StyleSheetAppState | null;
  pagePopups: PagePopupAppState[];
  redirectUrl: string | null;
};
const loadPageInner = apiAction<AppState, LoadPageBeginActionData, Api.LoadPageResponse, LoadPageSuccessActionData>({
  types: {
    begin: Actions.LoadPageBegin,
    failure: Actions.LoadPageFailure,
    success: [
      ({ dispatch, res }) => {
        if (res.redirectUrl) {
          goTo(res.redirectUrl);
          return;
        }

        dispatch({ type: Actions.LoadPageSuccess, data: res });

        scrollToY(0, 0);
      }
    ]
  },
  action: (state: AppState, dispatch, args: LoadPageBeginActionData) => {
    return api.loadPage({
      portalId: state.portal.id,
      baseUrl: globals.paths.apiUrl,
      path: args.path,
      styleSheetId: state.styleSheet.id,
      trackingParameters: args.trackingParameters
    });
  },
  mapResponse: (state: AppState, resp: Api.LoadPageResponse): LoadPageSuccessActionData => {
    const isDefaultCulture = resp.cultureCode === appInfo.primaryCultureCode;

    const styleSheet = resp.styleSheet ? mappers.styleSheet(resp.styleSheet, isDefaultCulture) : state.styleSheet;

    return {
      components: resp.components ? mappers.components(styleSheet, resp.components, isDefaultCulture) : null,
      content: mappers.content(styleSheet, resp.content, true, isDefaultCulture),
      funnelInfo: resp.funnelInfo,
      layoutContent: mappers.layoutContent(styleSheet, resp.layoutContent, isDefaultCulture),
      page: resp.page,
      pagePopups: mappers.pagePopups(styleSheet, resp.pagePopups, isDefaultCulture),
      popups: resp.popups ? mappers.popups(styleSheet, resp.popups, isDefaultCulture) : null,
      styleSheet: resp.styleSheet ? styleSheet : null,
      redirectUrl: resp.redirectUrl
    };
  }
});

export const loadPage = (path: string, qs: GenericMap<string>) => (dispatch: any, getState: () => AppState) => {
  processQueryParameters();
  dispatch(loadPageInner({ path, trackingParameters: pick(qs, [/^utm_/]) }));
};
